// DO NOT USE
import React, {useEffect, useCallback, useState} from 'react';
import clsx from 'clsx';

import {createStyles, withStyles, type WithStyles} from '@mui/styles';
import type {Theme} from '@netx/core-components/Theme';

import {SortableContainer} from 'react-sortable-hoc';

import {
	getInputFormObject,
	FormValidationErrors,
	FormValues,
	FormActions,
} from '@netx/core-components/Form';

import ScrollShadow from '../ScrollShadow';

import {useItemManagerStore, useItemManagerActions} from './ItemManagerService';

const styles = (theme: Theme) =>
	createStyles({
		root: {
			display: 'flex',
			flex: '1 0 auto',
			flexDirection: 'column',
			minHeight: 0,
			position: 'relative',
			width: '100%',
			'&.hasItems': {
				flex: '0 1 auto',
			},
		},
		wrapper: {
			flex: '1 1 auto',
			minHeight: 0,
			overflow: 'auto',
			'&.hasItems': {
				margin: theme.spacing(2, 0, 1, 0),
			},
		},
		disabled: {
			opacity: 0.5,
			...theme.mixins.disabled,
		},
		sorting: {
			'& *': {
				// pointerEvents: 'none',
			},
		},
	});

const getItemMatchRegExp = (id: number | string) => {
	return new RegExp(`\\[${id}\\]`);
};

/** Get item errors from form error entries */
const getItemErrors = (errors: FormValidationErrors, id: number | string) => {
	const itemErrors = {};
	const regex = getItemMatchRegExp(id);
	for (const key in errors) {
		if (!key.match(regex)) continue;
		// @ts-ignore
		itemErrors[key.replace(regex, '')] = errors[key];
	}
	return itemErrors;
};

/** Get item values from form value entries */
const getItemValues = (values: FormValues, id: number | string) => {
	const itemValues = {};
	const regex = getItemMatchRegExp(id);
	for (const key in values) {
		if (!key.match(regex)) continue;
		// @ts-ignore
		itemValues[key.replace(regex, '')] = values[key];
	}
	return itemValues;
};

const getItemActions = (actions: FormActions, id: number | string) => {
	const itemActions = {};
	const regex = getItemMatchRegExp(id);
	for (const key in actions) {
		if (!key.match(regex)) continue;
		// @ts-ignore
		itemActions[key.replace(regex, '')] = actions[key];
	}
	return itemActions;
};

export interface ItemManagerProps {
	className?: string;
	disabled?: boolean;
	fallback?: React.ReactNode;
	items?: any[];
	loading?: boolean;
	onRemoveClick?: (id: number | string) => boolean | void;
	scrollNewItemsIntoView?: boolean;
	ItemComponent: React.FunctionComponent<any> | React.ComponentType<any>;
	ItemComponentProps?: any;
}

const ItemManagerWrapper = SortableContainer(
	ScrollShadow(
		React.forwardRef<HTMLDivElement, React.PropsWithChildren<any>>(
			(props, ref) => {
				return <div {...props} ref={ref} />;
			},
		),
	),
);

/** @deprecated */
const ItemManager: React.FunctionComponent<
	WithStyles<typeof styles> & ItemManagerProps
> = ({
	className,
	classes,
	disabled,
	fallback,
	items,
	loading,
	onRemoveClick,
	scrollNewItemsIntoView = true,
	ItemComponent,
	ItemComponentProps,
}) => {
	const {form, removeItem, resetItems, state} = useItemManagerStore();
	const actions = useItemManagerActions();
	const [isSorting, setIsSorting] = useState(-1);

	const inputForm = getInputFormObject(form);

	useEffect(() => {
		resetItems(items || []);
	}, [items]);

	useEffect(() => {
		const sorting = isSorting > -1;
		document.body.style.cursor = sorting ? 'grabbing' : '';
		// @ts-ignore
		document.body.style['-webkit-touch-callout'] = sorting ? 'none' : '';
		// @ts-ignore
		document.body.style['-webkit-user-select'] = sorting ? 'none' : '';
		// @ts-ignore
		document.body.style['user-select'] = sorting ? 'none' : '';
	}, [isSorting]);

	const handleBeforeSortStart = useCallback(
		(sorting: any) => setIsSorting(sorting.index),
		[],
	);

	const handleRemoveClick = useCallback(
		(id: number | string) => {
			if (onRemoveClick && onRemoveClick(id) === false) return;
			removeItem(id);
		},
		[actions.remove],
	);

	const handleSortEnd = useCallback(
		({oldIndex, newIndex}: {oldIndex: number; newIndex: number}) => {
			setIsSorting(-1);
			if (oldIndex === newIndex) return;
			actions.reorder(oldIndex, newIndex);
		},
		[actions.reorder],
	);

	return (
		<div
			className={clsx(classes.root, className, {
				[classes.disabled]: disabled,
				[classes.sorting]: isSorting > -1,
				hasItems: !!state.items.length,
			})}
		>
			{loading ? <div></div> : null}
			{!loading && !!state.items.length ? (
				// @ts-ignore
				<ItemManagerWrapper
					lockToContainerEdges
					useDragHandle
					helperClass="dragging-item"
					lockAxis="y"
					onSortEnd={handleSortEnd}
					scrollNewItemsIntoView={scrollNewItemsIntoView}
					updateBeforeSortStart={handleBeforeSortStart}
					className={clsx(classes.wrapper, {
						hasItems: !!state.items.length,
					})}
				>
					{state.items.map((id, idx) => {
						return (
							<ItemComponent
								key={id}
								{...ItemComponentProps}
								sortable
								isSorting={isSorting === idx}
								id={id}
								index={idx}
								errors={getItemErrors(form.errors, id)}
								values={getItemValues(form.values, id)}
								actions={getItemActions(form.actions, id)}
								form={inputForm}
								onRemoveClick={handleRemoveClick}
							/>
						);
					})}
				</ItemManagerWrapper>
			) : (
				(!loading && fallback) || null
			)}
		</div>
	);
};

export default withStyles(styles)(
	React.memo(ItemManager) as unknown as (
		props: ItemManagerProps,
	) => JSX.Element,
);
